import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../redux/reducers";
import { Box, Card, Alert, Button, MenuItem, TextField } from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import SaveIcon from "@mui/icons-material/Save";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import SettingsIcon from "@mui/icons-material/Settings";
import DeleteForeverIcon from "@mui/icons-material/DeleteForever";

import NavTab from "../../layout/NavTab";
import PayrollShuttleTable, { EditablePayrollField } from "../../components/PayrollShuttle/PayrollShuttleTable";
import PayrollRatesDialog from "../../components/PayrollShuttle/PayrollRatesDialog";
import AlerteDialog from "../../components/AlerteDialog/AlerteDialog";
import { IPayrollShuttleEntry } from "../../interface/payrollShuttle";
import { getPayrollShuttle } from "../../tools/payrollShuttle/getPayrollShuttle";
import { generatePayrollShuttle } from "../../tools/payrollShuttle/generatePayrollShuttle";
import { savePayrollShuttle } from "../../tools/payrollShuttle/savePayrollShuttle";
import { exportPayrollShuttle } from "../../tools/payrollShuttle/exportPayrollShuttle";
import { deletePayrollShuttle } from "../../tools/payrollShuttle/deletePayrollShuttle";

const MONTH_LABELS = [
    "Janvier", "Février", "Mars", "Avril", "Mai", "Juin",
    "Juillet", "Août", "Septembre", "Octobre", "Novembre", "Décembre",
];

export default function PayrollShuttle() {
    const dispatch = useDispatch();
    const token = useSelector((state: RootState) => state.user.token);

    const now = new Date();
    const [year, setYear] = useState(now.getFullYear());
    const [month, setMonth] = useState(now.getMonth() + 1);
    const [entries, setEntries] = useState<IPayrollShuttleEntry[]>([]);
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(false);
    const [dirty, setDirty] = useState(false);
    const [ratesDialogOpen, setRatesDialogOpen] = useState(false);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

    const loadShuttle = () => {
        if (!token) return;
        setLoading(true);
        getPayrollShuttle(year, month, token, (data) => { setEntries(data); setDirty(false); }, dispatch, setError)
            .finally(() => setLoading(false));
    };

    useEffect(() => {
        loadShuttle();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [year, month]);

    const handleGenerate = () => {
        if (!token) return;
        setLoading(true);
        generatePayrollShuttle(year, month, token, (data) => { setEntries(data); setDirty(false); }, dispatch, setError)
            .finally(() => setLoading(false));
    };

    const handleSave = () => {
        if (!token) return;
        setLoading(true);
        savePayrollShuttle(year, month, entries, token, (data) => { setEntries(data); setDirty(false); }, dispatch, setError)
            .finally(() => setLoading(false));
    };

    const handleExport = () => {
        if (!token) return;
        exportPayrollShuttle(year, month, token, dispatch);
    };

    const handleDelete = () => {
        if (!token) return;
        setLoading(true);
        deletePayrollShuttle(year, month, token, (data) => { setEntries(data); setDirty(false); }, dispatch, setError)
            .finally(() => { setLoading(false); setDeleteDialogOpen(false); });
    };

    const handleFieldChange = (id: string, field: EditablePayrollField, value: string | number) => {
        setEntries((previousEntries) =>
            previousEntries.map((entry) =>
                entry._id === id ? ({ ...entry, [field]: value } as IPayrollShuttleEntry) : entry
            )
        );
        setDirty(true);
    };

    const yearOptions = [now.getFullYear() - 1, now.getFullYear(), now.getFullYear() + 1];

    return (
        <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
            <NavTab text="Navette de paie" />

            <Card elevation={0} sx={{ mx: 3, mb: 3, p: 2, borderRadius: "16px", border: "1px solid #e2e8f0", flexGrow: 1, display: "flex", flexDirection: "column" }}>
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3, gap: 2, flexWrap: "wrap" }}>
                    <Box sx={{ display: "flex", gap: 2 }}>
                        <TextField select label="Mois" value={month} onChange={(e) => setMonth(Number(e.target.value))} sx={{ width: 150 }}>
                            {MONTH_LABELS.map((label, index) => (
                                <MenuItem key={label} value={index + 1}>{label}</MenuItem>
                            ))}
                        </TextField>
                        <TextField select label="Année" value={year} onChange={(e) => setYear(Number(e.target.value))} sx={{ width: 120 }}>
                            {yearOptions.map((y) => (
                                <MenuItem key={y} value={y}>{y}</MenuItem>
                            ))}
                        </TextField>
                    </Box>

                    <Box sx={{ display: "flex", gap: 1 }}>
                        <Button variant="outlined" startIcon={<SettingsIcon />} onClick={() => setRatesDialogOpen(true)}>
                            Taux
                        </Button>
                        <Button variant="outlined" startIcon={<RefreshIcon />} onClick={handleGenerate} disabled={loading}>
                            Générer
                        </Button>
                        <Button variant="contained" startIcon={<SaveIcon />} onClick={handleSave} disabled={loading || !dirty}>
                            Enregistrer
                        </Button>
                        <Button variant="outlined" startIcon={<FileDownloadIcon />} onClick={handleExport} disabled={loading || entries.length === 0}>
                            Exporter
                        </Button>
                        <Button
                            variant="outlined"
                            color="error"
                            startIcon={<DeleteForeverIcon />}
                            onClick={() => setDeleteDialogOpen(true)}
                            disabled={loading || entries.length === 0}
                        >
                            Supprimer
                        </Button>
                    </Box>
                </Box>

                {error && <Alert severity="error" sx={{ mb: 2, borderRadius: "12px" }}>{error}</Alert>}

                <PayrollShuttleTable rows={entries} onFieldChange={handleFieldChange} />
            </Card>

            <AlerteDialog
                open={deleteDialogOpen}
                handleClose={() => setDeleteDialogOpen(false)}
                deleteProject={handleDelete}
                title="Supprimer la navette de paie ?"
                contentText={`Cette action supprimera définitivement la navette de paie de ${MONTH_LABELS[month - 1]} ${year} pour tous les salariés (saisies manuelles comprises). Cette action est irréversible.`}
                color="error"
            />

            <PayrollRatesDialog open={ratesDialogOpen} onClose={() => setRatesDialogOpen(false)} />
        </Box>
    );
}
